import React, { useContext, useState } from "react";
import { Button } from "@mui/material";
import { getDocs } from "firebase/firestore";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import PropertyContext from "../../contexts/PropertyContext";
import { propertyCollection } from "../../utils/firestorePaths";
import { drawReportHeader, stampPageNumbers } from "../../utils/reports/pdfChrome";
import { downloadBlob } from "../../utils/reports/downloadBlob";
import { buildReportFilename } from "../../utils/reports/reportFilename";
import { toDate } from "../../utils/reports/format";
import ReportDateRangeDialog from "./shared/ReportDateRangeDialog";

/**
 * Status Change Activity Log — who changed which room to what, over a date
 * range. Reads the daily propertyActionHistory documents written by the
 * logRoomStatusChange cloud trigger.
 */
export default function GenerateStatusActivityLog({ user }) {
  const { selectedProperty, selectedClient } = useContext(PropertyContext);
  const [open, setOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async ({ start, end }) => {
    setIsGenerating(true);
    try {
      const ref = propertyCollection(
        selectedClient,
        selectedProperty,
        "propertyActionHistory"
      );
      const snap = await getDocs(ref);

      // Flatten all action entries that fall within the range.
      const entries = [];
      snap.docs.forEach((d) => {
        const actions = d.data()?.actions || [];
        actions.forEach((a) => {
          const ts = toDate(a.timestamp);
          if (ts && ts >= start && ts <= end) {
            entries.push({ ...a, ts });
          }
        });
      });
      entries.sort((a, b) => a.ts - b.ts);

      if (entries.length === 0) {
        alert("No status changes found in that date range.");
        return;
      }

      const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
      const contentTop = drawReportHeader(pdf, {
        title: "Status Change Activity Log",
        dateRange: { start, end },
        generatedBy: user?.fullName || user?.personalEmail || "Unknown",
      });

      const body = entries.map((e) => [
        e.ts.toLocaleString(),
        e.roomDisplayName || e.roomId || "—",
        `${e.previousStatus || "—"} → ${e.newStatus || "—"}`,
        e.updatedBy || "Unknown",
      ]);

      autoTable(pdf, {
        startY: contentTop,
        head: [["Date / Time", "Room", "Change", "By"]],
        body,
        styles: { font: "helvetica", fontSize: 9, cellPadding: 2 },
        headStyles: { fillColor: [40, 40, 40], textColor: 255 },
        margin: { left: 12, right: 12 },
        theme: "striped",
      });

      stampPageNumbers(pdf);
      downloadBlob(
        pdf.output("blob"),
        buildReportFilename("Status_Activity_Log", { ext: "pdf", start, end })
      );
      setOpen(false);
    } catch (e) {
      console.error("Error generating Status Activity Log:", e);
      alert("Error generating report. See console for details.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <>
      <Button variant="contained" fullWidth onClick={() => setOpen(true)}>
        Status Activity Log
      </Button>
      <ReportDateRangeDialog
        open={open}
        title="Status Change Activity Log"
        description="Lists every room status change (who, when, from → to) in the selected date range."
        busy={isGenerating}
        onClose={() => setOpen(false)}
        onSubmit={handleGenerate}
      />
    </>
  );
}
